import { useMemo } from 'react';
import type { PersonaDto } from '@/lib/api/personas-client';

export interface PersonaFormOptions {
  /** Segments the workspace already uses; the field also accepts new ones. */
  segmentNameOptions: string[];
  /** Seeds the description when a known segment is chosen and nothing is typed yet. */
  segmentDescriptionByName: Record<string, string>;
  topicsOfInterestOptions: string[];
}

/**
 * The choices a persona form offers, drawn from what the workspace already has.
 *
 * Without this the segment and topic fields open empty, which reads as broken
 * rather than as a workspace with nothing in it yet.
 */
export function usePersonaFormOptions(personas: PersonaDto[] | undefined): PersonaFormOptions {
  return useMemo(() => {
    const segmentDescriptionByName: Record<string, string> = {};

    for (const persona of personas ?? []) {
      const name = persona.segmentName ?? '';
      const description = persona.segmentDescription ?? '';
      // First description wins: later personas in the same segment describe the
      // same thing, and re-reading them would only churn the seeded value.
      if (name && description && !segmentDescriptionByName[name]) {
        segmentDescriptionByName[name] = description;
      }
    }

    return {
      segmentNameOptions: [
        ...new Set((personas ?? []).map((persona) => persona.segmentName ?? '').filter(Boolean)),
      ],
      segmentDescriptionByName,
      topicsOfInterestOptions: [
        ...new Set(
          (personas ?? []).map((persona) => persona.topicOfInterest ?? '').filter(Boolean),
        ),
      ],
    };
  }, [personas]);
}
